import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  Edit2, 
  Calendar, 
  DollarSign, 
  User, 
  Building, 
  Phone, 
  Mail, 
  Globe, 
  Target,
  Star,
  Activity,
  Clock,
  Trophy,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  FileText,
  Award,
  Briefcase,
  BarChart3,
  Users,
  ExternalLink
} from 'lucide-react';
import AttachmentServiceV2 from '../../components/Attachments/AttachmentServiceV2';
import entityRelationService from '../../services/entityRelationService';
import EntityActivities from '../../components/EntityManagement/EntityActivities';
import CustomAttributesDisplay from '../../components/CustomAttributes/CustomAttributesDisplay';
import { extractCustomAttributeFields } from '../../components/CustomAttributes/utils';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { useAuth } from '../../contexts/AuthContext';

const OpportunityDetailModal = ({ opportunity, isOpen, onClose, onEdit }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tenantCode =
    user?.selectedTenant?.code ||
    user?.selectedTenant?.tenant_code ||
    user?.tenantCode ||
    null;
  const { config: entityConfig } = useEntityConfig('opportunity');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [opportunityData, setOpportunityData] = useState(opportunity);
  const [entityRelationsUnified, setEntityRelationsUnified] = useState([]);

  const customAttributeFields = React.useMemo(() => {
    if (!entityConfig) return [];
    try {
      return extractCustomAttributeFields(entityConfig, {
        tenantCode,
        includeGlobal: true
      });
    } catch (err) {
      console.error('OpportunityDetailModal: Failed to extract custom attribute fields', err);
      return [];
    }
  }, [entityConfig, tenantCode]);

  const opportunityAttributes = React.useMemo(() => {
    const target = opportunityData || opportunity;
    const raw = target?.Attributes ?? target?.attributes;
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw) || {};
      } catch (err) {
        console.warn('OpportunityDetailModal: Failed to parse attributes JSON', err);
        return {};
      }
    }
    return {};
  }, [opportunityData, opportunity]);

  useEffect(() => {
    if (isOpen && opportunity && !opportunityData) {
      fetchOpportunityDetails();
    } else if (opportunity) {
      setOpportunityData(opportunity);
    }
  }, [isOpen, opportunity]);

  // Load unified entity relations when modal opens and data is available
  useEffect(() => {
    const loadRelations = async () => {
      const opportunityId = opportunityData?.OpportunityID || opportunity?.OpportunityID;
      if (!isOpen || !opportunityId) return;
      try {
        const relationsResp = await entityRelationService.getRelatedEntities('Opportunity', Number(opportunityId), { direction: 'both', timeoutMs: 10000 });
        const relations = Array.isArray(relationsResp) ? relationsResp : (relationsResp?.relations || []);
        const mapped = relations.map(rel => {
          const srcType = rel.SourceEntityType || rel.sourceEntityType;
          const tgtType = rel.TargetEntityType || rel.targetEntityType;
          const srcId = rel.SourceEntityID || rel.sourceEntityID;
          const tgtId = rel.TargetEntityID || rel.targetEntityID;
          const isOpportunitySource = (srcType || '').toString().toLowerCase() === 'opportunity';
          const entityType = (isOpportunitySource ? tgtType : srcType) || '';
          const entityId = isOpportunitySource ? tgtId : srcId;
          return {
            EntityType: entityType,
            EntityID: entityId,
            RelationID: rel.RelationID || rel.relationId,
            RelationType: rel.RelationType || rel.relationType
          };
        });
        setEntityRelationsUnified(mapped);
      } catch {
        setEntityRelationsUnified([]);
      }
    };
    loadRelations();
  }, [isOpen, opportunityData?.OpportunityID]);

  // Helpers for entity relations UI (icons and colors)
  const getEntityColors = (entityType) => {
    switch (entityType) {
      case 'Customer':
        // Customer (default to Company = green)
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      case 'Lead':
        // Lead = red
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          icon: 'text-red-600 dark:text-red-400',
          badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
        };
      case 'Opportunity':
        // Opportunity = violet
        return {
          bg: 'bg-violet-50 dark:bg-violet-900/20',
          border: 'border-violet-200 dark:border-violet-800',
          icon: 'text-violet-600 dark:text-violet-400',
          badge: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200'
        };
      case 'Activity':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          icon: 'text-orange-600 dark:text-orange-400',
          badge: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
        };
      case 'SaleNote':
        // SaleNote = yellow
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          icon: 'text-yellow-600 dark:text-yellow-400',
          badge: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-700',
          border: 'border-gray-200 dark:border-gray-700',
          icon: 'text-gray-600 dark:text-gray-400',
          badge: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
        };
    }
  };

  const getEntityIcon = (entityType) => {
    const colors = getEntityColors(entityType);
    const iconClass = `h-3 w-3 ${colors.icon}`;
    
    switch (entityType) {
      case 'Customer': return <Building className={iconClass} />;
      case 'Lead': return <User className={iconClass} />;
      case 'Opportunity': return <Target className={iconClass} />;
      case 'Activity': return <Calendar className={iconClass} />;
      case 'SaleNote': return <FileText className={iconClass} />;
      default: return <FileText className={iconClass} />;
    }
  };

  const fetchOpportunityDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/opportunities/${opportunity.OpportunityID}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch opportunity details');
      }

      const data = await response.json();
      setOpportunityData(data.opportunity || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(opportunityData);
    }
  };

  const handleClose = () => {
    setError(null);
    setOpportunityData(null);
    onClose();
  };

  if (!isOpen) return null;

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Lead': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'Qualified': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300';
      case 'Proposal': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-300';
      case 'Negotiation': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/60 dark:text-orange-300';
      case 'Closed Won': return 'bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-300';
      case 'Closed Lost': return 'bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/60 dark:text-white dark:border-red-700/60';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/60 dark:text-white dark:border-orange-700/60';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-amber-900/60 dark:text-white dark:border-amber-700/60';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200 dark:bg-emerald-900/60 dark:text-white dark:border-emerald-700/60';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-800 dark:text-white dark:border-slate-600';
    }
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 80) return 'text-green-600 dark:text-green-400';
    if (probability >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (probability >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'Lead': return Target;
      case 'Qualified': return CheckCircle;
      case 'Proposal': return FileText;
      case 'Negotiation': return TrendingUp;
      case 'Closed Won': return Trophy;
      case 'Closed Lost': return AlertCircle;
      default: return Activity;
    }
  };

  const formatOpportunityName = (opportunity) => {
    return opportunity.OpportunityName || `Opportunity #${opportunity.OpportunityID}`;
  };

  const StageIcon = getStageIcon(opportunityData?.Stage);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
          <button
            onClick={handleClose}
            className="mt-4 w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!opportunityData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <p className="text-gray-600 dark:text-gray-400">{t('opportunities.opportunityNotFound', 'Opportunity not found')}</p>
          </div>
          <button
            onClick={handleClose}
            className="mt-4 w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
        {/* Compact Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${getStageColor(opportunityData.Stage)}`}>
                <StageIcon className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{formatOpportunityName(opportunityData)}</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {opportunityData.CustomerName} • {opportunityData.ContactName}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleEdit}
              className="px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2 text-sm"
            >
              <Edit2 className="h-4 w-4" />
              <span>{t('common.edit')}</span>
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-80px)]">
          <div className="p-4">
            {/* Key Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t('opportunities.value')}</p>
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(opportunityData.Value, opportunityData.Currency)}
                    </p>
                  </div>
                  <DollarSign className="h-5 w-5 text-green-500 dark:text-green-400" />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t('opportunities.probability')}</p>
                    <p className={`text-lg font-bold ${getProbabilityColor(opportunityData.Probability)}`}>
                      {opportunityData.Probability}%
                    </p>
                  </div>
                  <Star className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t('opportunities.stage')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{opportunityData.Stage}</p>
                  </div>
                  <StageIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t('opportunities.expectedCloseDate')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatDate(opportunityData.ExpectedCloseDate)}
                    </p>
                  </div>
                  <Calendar className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                </div>
              </div>
            </div>

            {/* Status Badges */}
            <div className="flex items-center space-x-2 mb-4">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(opportunityData.Stage)}`}>
                {opportunityData.Stage}
              </span>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(opportunityData.Priority)}`}>
                {opportunityData.Priority}
              </span>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                opportunityData.IsActive ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
              }`}>
                {opportunityData.IsActive ? 'Active' : 'Inactive'}
              </span>
              {opportunityData.IsHighValue && (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                  High Value
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Main Content - 3 columns */}
              <div className="lg:col-span-3 space-y-4">
                {/* Basic & Contact Info Combined */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Target className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('opportunities.basicInfo', 'Basic Information')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('opportunities.opportunityName', 'Opportunity Name')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{opportunityData.OpportunityName || '-'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('opportunities.category')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{opportunityData.Category || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('opportunities.industry')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{opportunityData.Industry || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('opportunities.leadSource')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{opportunityData.LeadSource || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('opportunities.salesTeam')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{opportunityData.SalesTeam || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('opportunities.estimatedEffort')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{opportunityData.EstimatedEffort ? `${opportunityData.EstimatedEffort} hours` : '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Users className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('opportunities.contactInfo', 'Contact Information')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('opportunities.customerName')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{opportunityData.CustomerName || '-'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('opportunities.contactName')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{opportunityData.ContactName || '-'}</p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{t('opportunities.contactEmail')}</p>
                        <p className="text-sm text-gray-900 dark:text-white">{opportunityData.ContactEmail || '-'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{t('opportunities.contactPhone')}</p>
                        <p className="text-sm text-gray-900 dark:text-white">{opportunityData.ContactPhone || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('opportunities.timeline', 'Timeline')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('opportunities.createdAt')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(opportunityData.CreatedAt)}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('opportunities.lastActivityDate')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(opportunityData.LastActivityDate)}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('opportunities.nextFollowUpDate')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(opportunityData.NextFollowUpDate)}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('opportunities.expectedCloseDate')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(opportunityData.ExpectedCloseDate)}</p>
                    </div>
                  </div>
                </div>

                {/* Notes and Details */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('opportunities.notesAndDetails', 'Notes & Details')}</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('opportunities.description')}</p>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunityData.Description || '-'}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('opportunities.notes')}</p>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunityData.Notes || '-'}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('opportunities.requirements')}</p>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunityData.Requirements || '-'}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('opportunities.nextStepAction')}</p>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunityData.NextStepAction || '-'}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('opportunities.competitorInfo')}</p>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{opportunityData.CompetitorInfo || '-'}</p>
                    </div>
                  </div>
                </div>

                {customAttributeFields.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                    <CustomAttributesDisplay
                      fields={customAttributeFields}
                      attributes={opportunityAttributes}
                      title={t('opportunities.additionalDataSection', 'Opportunity Additional Data')}
                      entityName="opportunity"
                      onFieldsUpdated={async () => {
                        if (opportunity?.OpportunityID) {
                          window.location.reload();
                        }
                      }}
                    />
                  </div>
                )}

                {/* Activities */}
                <EntityActivities
                  entityType="Opportunity"
                  entityId={opportunityData.OpportunityID}
                  entityName={formatOpportunityName(opportunityData)}
                />

                {/* Attachments */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <AttachmentServiceV2 
                    entityType="Opportunity" 
                    entityId={opportunityData.OpportunityID} 
                    onAttachmentChange={() => {
                      // Optional: Refresh opportunity data or show notification
                    }}
                  />
                </div>
              </div>

              {/* Compact Sidebar - 1 column */}
              <div className="space-y-4">
                {/* Entity Relations */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Users className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('common.relatedItems', 'Relationships')}</h3>
                    {entityRelationsUnified.length > 0 && (
                      <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs px-2 py-0.5 rounded-full">{entityRelationsUnified.length}</span>
                    )}
                  </div>
                  {entityRelationsUnified.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('opportunities.noEntityRelations', 'No entity relations')}</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {entityRelationsUnified.map((rel, idx) => {
                        const colors = getEntityColors(rel.EntityType);
                        return (
                          <div 
                            key={rel.RelationID || `${rel.EntityType}-${rel.EntityID}-${idx}`} 
                            className={`border ${colors.border} ${colors.bg} rounded-lg p-2 transition-colors`}
                          >
                            <div className="flex items-center space-x-2">
                              {getEntityIcon(rel.EntityType)}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${colors.badge}`}>{rel.EntityType}</span>
                                  <span className="text-xs text-gray-900 dark:text-white">#{rel.EntityID}</span>
                                </div>
                                {rel.RelationType && (
                                  <div className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">{rel.RelationType}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Stage Progress */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <TrendingUp className="h-4 w-4 text-blue-400" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('opportunities.stageProgress', 'Stage Progress')}</h3>
                  </div>
                  <div className="text-center">
                    <div className={`p-3 rounded-lg ${getStageColor(opportunityData.Stage)}`}>
                      <StageIcon className="h-6 w-6 mx-auto mb-1" />
                      <p className="text-sm font-semibold">{opportunityData.Stage}</p>
                    </div>
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${opportunityData.Probability || 0}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opportunityData.Probability || 0}% probability</p>
                    </div>
                  </div>
                </div>

                {/* Value Information */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <DollarSign className="h-4 w-4 text-green-400" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('opportunities.valueInfo', 'Value Information')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(opportunityData.Value, opportunityData.Currency)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Total Value</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-center">
                      <div>
                        <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">{opportunityData.Probability || 0}%</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Probability</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                          {opportunityData.Value && opportunityData.Probability 
                            ? formatCurrency((opportunityData.Value * opportunityData.Probability) / 100, opportunityData.Currency)
                            : '-'
                          }
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Expected Value</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Priority & Status */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Star className="h-4 w-4 text-yellow-400" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('opportunities.priorityStatus', 'Priority & Status')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="text-center">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getPriorityColor(opportunityData.Priority)}`}>
                        {opportunityData.Priority || 'Not Set'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          opportunityData.IsActive ? 'bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {opportunityData.IsActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          opportunityData.IsHighValue ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/60 dark:text-purple-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {opportunityData.IsHighValue ? 'High Value' : 'Standard'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetailModal;
